import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { WordSetCategoryType } from 'app/types';
import FilledActionButton from 'components/FilledActionButton';
import { VocabularyTestScope } from 'pages/VocabularyTest/useVocabularyTestScope';

const pluralizeSubCategoriesNom = (n: number): string => {
  if (n === 1) return 'підкатегорія';
  if (n < 5) return 'підкатегорії';
  return 'підкатегорій';
};

const pluralizeCategoriesLoc = (n: number): string => {
  if (n === 1) return 'категорії';
  return 'категоріях';
};

interface VocabularyTestScopePanelProps {
  scope: VocabularyTestScope;
  onClose: () => void;
  onStart: () => void;
  startLabel: string;
  starting?: boolean;
}

const VocabularyTestScopePanel: React.FC<VocabularyTestScopePanelProps> = ({
  scope,
  onClose,
  onStart,
  startLabel,
  starting,
}) => {
  const { activeCategory, selectedWordSetType } = scope;

  const selectedWordSet =
    scope.wordSetOptions.find((o) => o.wordSetType === selectedWordSetType) ||
    scope.wordSetOptions[0];

  const soleFullWordSetLabel = scope.soleFullWordSetType
    ? scope.wordSetOptions.find(
        (o) => o.wordSetType === scope.soleFullWordSetType,
      )?.label
    : undefined;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.main',
        borderRadius: 2,
        boxShadow: '0 18px 44px -30px rgba(59,44,147,0.5)',
        mt: 2,
        overflow: 'hidden',
      }}
    >
      {/* header: title + word-set picker */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={'flex-start'}
        justifyContent={'space-between'}
        spacing={2}
        sx={{ p: '15px 20px 13px 22px' }}
      >
        <Box minWidth={0}>
          <Typography sx={{ fontSize: 17, fontWeight: 800, lineHeight: 1.15 }}>
            {'Категорії для тестування'}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.5 }}>
            {'Оберіть набір слів, а потім конкретні категорії або підкатегорії для тесту.'}
          </Typography>
        </Box>
        <TextField
          label={'Список слів'}
          onChange={(event) =>
            scope.setSelectedWordSetType(
              event.target.value as WordSetCategoryType,
            )
          }
          SelectProps={{ MenuProps: { disableScrollLock: true } }}
          select
          size={'small'}
          sx={{ flex: 'none', minWidth: { xs: '100%', md: 300 } }}
          value={selectedWordSetType}
        >
          {scope.wordSetOptions.map(({ label, wordSetType }) => (
            <MenuItem key={wordSetType} value={wordSetType}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* two columns: categories / subcategories */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'primary.tableBorder',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '340px 1fr' },
        }}
      >
        <Box
          sx={{
            borderBottom: { xs: '1px solid', md: 0 },
            borderColor: 'primary.tableBorder',
            borderRight: { xs: 0, md: '1px solid' },
            maxHeight: 360,
            overflowY: 'auto',
          }}
        >
          <Box sx={{ p: '14px 20px 10px' }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
              {selectedWordSet.label}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.25 }}>
              {`${scope.categories.length} категорій`}
            </Typography>
          </Box>
          {scope.categories.map(({ categoryName, title }) => {
            const active = categoryName === scope.activeCategoryName;
            return (
              <Box
                key={categoryName}
                onClick={() => scope.setActiveCategoryName(categoryName)}
                sx={{
                  alignItems: 'center',
                  bgcolor: active ? 'primary.body' : 'transparent',
                  borderTop: '1px solid',
                  borderColor: 'primary.tableBorder',
                  cursor: 'pointer',
                  display: 'flex',
                  gap: 1.5,
                  px: 2.25,
                  py: 1.25,
                }}
              >
                <Checkbox
                  checked={scope.isCategoryFullySelected(
                    selectedWordSetType,
                    categoryName,
                  )}
                  indeterminate={scope.isCategoryPartiallySelected(
                    selectedWordSetType,
                    categoryName,
                  )}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() =>
                    scope.toggleCategory(selectedWordSetType, categoryName)
                  }
                  size={'small'}
                />
                <Typography fontWeight={active ? 800 : 600}>
                  {title || categoryName}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {!activeCategory ? (
            <Typography color={'text.secondary'} sx={{ p: 3 }}>
              {'Оберіть категорію, щоб побачити підкатегорії.'}
            </Typography>
          ) : (
            <React.Fragment>
              <Stack
                direction={'row'}
                alignItems={'flex-start'}
                justifyContent={'space-between'}
                spacing={2}
                sx={{
                  bgcolor: 'background.paper',
                  p: '14px 22px 10px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
                    {activeCategory.title}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.25 }}>
                    {'Підкатегорії для вибраної категорії'}
                  </Typography>
                </Box>
                <Button
                  variant={'outlined'}
                  size={'small'}
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={() =>
                    scope.toggleCategory(
                      selectedWordSetType,
                      activeCategory.categoryName,
                    )
                  }
                >
                  {scope.isCategoryFullySelected(
                    selectedWordSetType,
                    activeCategory.categoryName,
                  )
                    ? 'Зняти всі'
                    : 'Обрати всі'}
                </Button>
              </Stack>

              {scope.isLoadingSubCategories(
                selectedWordSetType,
                activeCategory.categoryName,
              ) &&
              !scope.getSubCategories(
                selectedWordSetType,
                activeCategory.categoryName,
              ).length ? (
                <Stack alignItems={'center'} justifyContent={'center'} minHeight={180}>
                  <CircularProgress size={28} />
                </Stack>
              ) : (
                scope
                  .getSubCategories(
                    selectedWordSetType,
                    activeCategory.categoryName,
                  )
                  .map(({ subcategoryName }) => (
                    <Box
                      key={subcategoryName}
                      onClick={() =>
                        scope.toggleSubCategory(
                          selectedWordSetType,
                          activeCategory.categoryName,
                          subcategoryName,
                        )
                      }
                      sx={{
                        alignItems: 'center',
                        borderTop: '1px solid',
                        borderColor: 'primary.tableBorder',
                        cursor: 'pointer',
                        display: 'flex',
                        gap: 2,
                        p: '10px 22px',
                      }}
                    >
                      <Checkbox
                        checked={scope.isSubCategorySelected(
                          selectedWordSetType,
                          activeCategory.categoryName,
                          subcategoryName,
                        )}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() =>
                          scope.toggleSubCategory(
                            selectedWordSetType,
                            activeCategory.categoryName,
                            subcategoryName,
                          )
                        }
                        size={'small'}
                      />
                      <Typography>{subcategoryName}</Typography>
                    </Box>
                  ))
              )}
            </React.Fragment>
          )}
        </Box>
      </Box>

      {/* sticky footer: selection summary + reset + collapse + start */}
      <Stack
        direction={'row'}
        alignItems={'center'}
        spacing={2}
        sx={{
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'primary.tableBorder',
          bottom: 0,
          flexWrap: 'wrap',
          position: 'sticky',
          p: '11px 22px',
          rowGap: 1.5,
        }}
      >
        <Typography sx={{ color: 'text.secondary', fontSize: 13.5 }}>
          {!scope.selectedCount ? (
            'Нічого не обрано — познач категорії, щоб додати їх у тест'
          ) : soleFullWordSetLabel ? (
            <React.Fragment>
              {'Обрано '}
              <Box component={'b'} sx={{ color: 'text.primary', fontWeight: 800 }}>
                {'усі слова'}
              </Box>
              {` з «${soleFullWordSetLabel}»`}
            </React.Fragment>
          ) : (
            <React.Fragment>
              {'Обрано '}
              <Box component={'b'} sx={{ color: 'text.primary', fontWeight: 800 }}>
                {scope.selectedCount}
              </Box>
              {` ${pluralizeSubCategoriesNom(scope.selectedCount)} у `}
              <Box component={'b'} sx={{ color: 'text.primary', fontWeight: 800 }}>
                {scope.catCount}
              </Box>
              {` ${pluralizeCategoriesLoc(scope.catCount)}`}
            </React.Fragment>
          )}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {!scope.isDefault && (
          <Button variant={'text'} onClick={scope.reset}>
            {'Скинути'}
          </Button>
        )}
        <Button variant={'text'} onClick={onClose}>
          {'Згорнути'}
        </Button>
        <FilledActionButton
          startIcon={<PlayArrowIcon />}
          onClick={onStart}
          disabled={starting}
          sx={{ fontSize: 14, height: 42, px: 2.75 }}
        >
          {startLabel}
        </FilledActionButton>
      </Stack>
    </Box>
  );
};

export default VocabularyTestScopePanel;
